// VideoElementPool.js
'use strict';
import PriorityPool from '../resource/PriorityPool.js';
import { player as playerDebug } from '../resource/debug.js';

const poolDebug = playerDebug.extend('log:VideoElementPool');
const poolError = playerDebug.extend('error:VideoElementPool');

const DEFAULT_VIDEO_ELEMENT_POOL_SIZE_MAX = 5;
let VIDEO_ELEMENT_POOL_SIZE_MAX =
  Number(process.env.VIDEO_ELEMENT_POOL_SIZE_DESKTOP_MAX) ||
  DEFAULT_VIDEO_ELEMENT_POOL_SIZE_MAX;

const createVideoElement = elementId => {
  const element = document.createElement('video');
  element.id = elementId;
  element.style.cssText = 'width: 100%; height: 100%;';
  element.playsInline = true;
  element.setAttribute('playsinline', '');
  element.setAttribute('webkit-playsinline', '');
  return element;
};

const showVideoElement = element => {
  element.removeAttribute('hidden');
  element.style.cssText =
    'width: 100%; height: 100%; display: block !important; opacity: 1;';
};

const resetVideoElement = element => {
  element.pause?.();
  element.removeAttribute('src');
  element.srcObject = null;
  element.load?.();
  element.remove();
  element.muted = false;
  element.loop = false;
  element.style.cssText = 'width: 100%; height: 100%;';
};

class VideoElementPool {
  constructor({ poolSize = VIDEO_ELEMENT_POOL_SIZE_MAX } = {}) {
    /** @type {HTMLVideoElement[]} */
    this._availableElements = [];
    /** @type {Map<string, () => void>} */
    this._signalCleanups = new Map();
    this._createdCount = 0;

    for (let index = 0; index < poolSize; index++) {
      this._availableElements.push(
        createVideoElement(`video-${this._createdCount++}`)
      );
    }

    this._pool = new PriorityPool({
      maxSize: poolSize,
      onAllocate: (id, poolResource) => {
        const element = this._availableElements.shift();
        if (!element) {
          throw new Error(
            `VideoElementPool: no available element for ${id} (pool exhausted)`
          );
        }
        showVideoElement(element);
        poolResource.element = element;
      },
      onRelease: async (_id, poolResource) => {
        const { element, onBeforeRelease } = poolResource;
        if (onBeforeRelease) {
          try {
            await onBeforeRelease();
          } catch (err) {
            poolError(
              `release:onBeforeReleaseError: ${element?.id}: ${err?.message || String(err)}`
            );
          }
        }
        if (!element) return;

        resetVideoElement(element);

        try {
          if (element.mediaKeys) {
            await element.setMediaKeys(null);
          }
          this._availableElements.push(element);
        } catch (error) {
          poolError(
            `release:cleanupError: ${element.id}: ${error?.message || String(error)}`
          );
          this._availableElements.push(createVideoElement(element.id));
        }
      },
      debugNameSpace: 'VideoElementPool',
    });

    poolDebug(`init:done: initialized with ${poolSize} video elements`);
  }

  acquire(key, { priority, onPreempted, signal, onBeforeRelease }) {
    if (signal?.aborted) {
      const error = new Error('VideoElementPool acquire aborted');
      error.name = 'AbortError';
      return Promise.reject(error);
    }

    this._signalCleanups.get(key)?.();
    this._signalCleanups.delete(key);

    if (signal) {
      const onAbort = () => this.cancelAcquire(key);
      signal.addEventListener('abort', onAbort, { once: true });
      this._signalCleanups.set(key, () =>
        signal.removeEventListener('abort', onAbort)
      );
    }

    return this._pool
      .acquire(key, {
        priority,
        onPreempted,
        onBeforeRelease,
      })
      .then(({ poolResource }) => poolResource.element);
  }

  cancelAcquire(key) {
    this._signalCleanups.get(key)?.();
    this._signalCleanups.delete(key);
    this._pool.release(key);
  }

  release(key) {
    this._signalCleanups.get(key)?.();
    this._signalCleanups.delete(key);
    return this._pool.release(key);
  }

  updatePriority(key, priority) {
    this._pool.updatePriority(key, priority);
  }

  setMaxSize(size) {
    const previousMax = this._pool.getPoolStatus().maxSize;
    this._pool.setMaxSize(size);
    if (size > previousMax) {
      for (let index = previousMax; index < size; index++) {
        this._availableElements.push(
          createVideoElement(`video-${this._createdCount++}`)
        );
      }
    }
  }

  async destroy() {
    for (const cleanup of this._signalCleanups.values()) {
      cleanup();
    }
    this._signalCleanups.clear();
    await this._pool.releaseAll();
    this._availableElements = [];
  }
}

let _videoElementPoolInstance = null;

export const setVideoElementPoolSizeMax = ({ size }) => {
  if (typeof size !== 'number' || size <= 0) return;
  VIDEO_ELEMENT_POOL_SIZE_MAX = size;
  poolDebug('setVideoElementPoolSizeMax', { size });
  _videoElementPoolInstance?.setMaxSize(size);
};

export const getVideoElementPool = (options = {}) => {
  if (_videoElementPoolInstance) return _videoElementPoolInstance;

  _videoElementPoolInstance = new VideoElementPool(options);

  if (typeof window !== 'undefined') {
    window.__videoElementPool__ = _videoElementPoolInstance;
  }
  return _videoElementPoolInstance;
};

export { VideoElementPool };
